import React, { useState } from 'react';
import { authPost } from '../lib/authFetch';
import './PaywallGate.css';

function formatPrice(amount, currency, interval) {
  if (amount == null) return '';
  const symbol = (currency || 'usd').toUpperCase() === 'USD' ? '$' : `${(currency || '').toUpperCase()} `;
  const whole = amount % 100 === 0;
  const value = whole ? (amount / 100).toFixed(0) : (amount / 100).toFixed(2);
  return `${symbol}${value}${interval ? `/${interval}` : ''}`;
}

function PaywallGate({ user, onComplete, onSignOut }) {
  const [proLoading, setProLoading] = useState(false);
  const [error, setError] = useState('');
  const [code, setCode] = useState('');
  const [codeChecking, setCodeChecking] = useState(false);
  const [codeApplied, setCodeApplied] = useState(false);
  const [codeError, setCodeError] = useState('');
  const [priceInfo, setPriceInfo] = useState(null);

  const handleApplyCode = async () => {
    const trimmed = code.trim();
    if (!trimmed) return;
    setCodeChecking(true);
    setCodeError('');
    try {
      const response = await authPost('/api/redeem-code', { code: trimmed });
      const data = await response.json();
      if (data.valid) {
        setCodeApplied(true);
        setPriceInfo(data);
      } else {
        setCodeApplied(false);
        setPriceInfo(null);
        setCodeError(data.error || 'Invalid code');
      }
    } catch (err) {
      console.error('Error redeeming code:', err);
      setCodeApplied(false);
      setPriceInfo(null);
      setCodeError('Failed to check code. Please try again.');
    } finally {
      setCodeChecking(false);
    }
  };

  const handleSubscribe = async () => {
    setProLoading(true);
    setError('');
    try {
      const response = await authPost('/api/create-checkout-session', {
        ...(codeApplied ? { code: code.trim() } : {})
      });
      const data = await response.json();
      if (data.url) {
        localStorage.setItem('penelope-checkout-user', user.id);
        window.location.href = data.url;
      } else {
        throw new Error(data.error || 'Failed to create checkout session');
      }
    } catch (err) {
      console.error('Error creating checkout session:', err);
      setError('Failed to start checkout. Please try again.');
      setProLoading(false);
    }
  };

  const handleStartTrial = async () => {
    setProLoading(true);
    setError('');
    try {
      const response = await authPost('/api/activate-trial-code', { code: code.trim() });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to activate trial');
      }
      onComplete();
    } catch (err) {
      console.error('Error activating trial:', err);
      setError('Failed to start trial. Please try again.');
      setProLoading(false);
    }
  };

  const isTrialCode = codeApplied && priceInfo?.kind === 'trial';

  return (
    <div className="paywall-gate">
      <div className="paywall-header">
        <div className="paywall-logo">
          <span className="paywall-logo-text">knowroom</span>
        </div>
        <h1 className="paywall-title">Choose your plan</h1>
      </div>

      {error && (
        <div className="paywall-error">{error}</div>
      )}

      <div className="paywall-cards paywall-cards-single">
        {/* Pro Solo Card */}
        <div className="paywall-card paywall-card-pro">
          <h2 className="paywall-card-title">Knowroom Solo</h2>

          {codeApplied && priceInfo?.kind === 'price' ? (
            <div className="paywall-card-price">
              <span className="price-amount">{formatPrice(priceInfo.amount, priceInfo.currency, null)}</span>
              <span className="price-period">/{priceInfo.interval || 'month'}</span>
            </div>
          ) : isTrialCode ? (
            <div className="paywall-card-price">
              <span className="price-amount">Free</span>
              <span className="price-period">for {priceInfo.trialDays} days</span>
            </div>
          ) : (
            <p className="paywall-card-desc paywall-card-desc-lead">Enter your code below to see your price</p>
          )}

          <ul className="paywall-card-features">
            <li>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              Unlimited people
            </li>
            <li>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              AI-powered insights
            </li>
            <li>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              Document uploads
            </li>
            <li>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              Priority support
            </li>
            <li>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              Cancel anytime
            </li>
          </ul>

          <div className="paywall-code-row">
            <input
              type="text"
              className="paywall-code-input"
              placeholder="Enter your code"
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                setCodeApplied(false);
                setPriceInfo(null);
                setCodeError('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleApplyCode();
                }
              }}
              disabled={codeApplied}
            />
            <button
              type="button"
              className="paywall-code-apply"
              onClick={handleApplyCode}
              disabled={codeChecking || codeApplied || !code.trim()}
            >
              {codeApplied ? 'Applied' : codeChecking ? 'Checking...' : 'Apply'}
            </button>
          </div>
          {codeError && <p className="paywall-code-error">{codeError}</p>}

          <button
            className="paywall-card-button paywall-card-button-primary"
            onClick={isTrialCode ? handleStartTrial : handleSubscribe}
            disabled={proLoading || !codeApplied}
          >
            {proLoading ? 'Loading...' : isTrialCode ? 'Start Free Trial' : 'Subscribe Now'}
          </button>
        </div>
      </div>

      {onSignOut && (
        <p className="paywall-footer">
          <button onClick={onSignOut} className="paywall-signout">Sign out</button>
        </p>
      )}
    </div>
  );
}

export default PaywallGate;
